/**
 * ModulesContext - Gestão centralizada de módulos via REST API
 *
 * Este context substitui a gestão estática de módulos por uma
 * integração dinâmica com a REST API Node.js do backend.
 *
 * NOTE: activeModules here are full Module objects (for config UI).
 * AuthContext (parent provider) holds module keys as string[] (for access control).
 * Keep both sources in sync by fetching from the same backend endpoint.
 */
import React, { createContext, useContext, ReactNode } from "react";
import { useModulos } from "@/hooks/api/useModulos";

interface Module {
  id: number;
  module_key: string;
  name: string;
  description?: string;
  category: string;
  is_active: boolean;
  dependencies?: string[];
}

interface ModulesContextData {
  modules: Module[];
  isLoading: boolean;
  activeModules: Module[];
  isModuleActive: (moduleKey: string) => boolean;
  getModulesByCategory: (category: string) => Module[];
  toggleModule: (moduleId: number) => void;
  isTogglingModule: boolean;
  dependencies: Record<string, any>;
}

const ModulesContext = createContext<ModulesContextData | undefined>(undefined);

interface ModulesProviderProps {
  children: ReactNode;
}

export function ModulesProvider({ children }: ModulesProviderProps) {
  const {
    modules,
    activeModules,
    isLoading,
    toggleModule,
    isToggling,
    dependencies,
    isModuleActive,
    getModulesByCategory,
  } = useModulos();

  return (
    <ModulesContext.Provider
      value={{
        modules,
        isLoading,
        activeModules,
        isModuleActive,
        getModulesByCategory,
        toggleModule,
        isTogglingModule: isToggling,
        // @ts-expect-error — TS2322
        dependencies,
      }}
    >
      {children}
    </ModulesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useModulesContext() {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error("useModulesContext must be used within ModulesProvider");
  }
  return context;
}
